import React, { useMemo } from 'react';
import {
    Modal,
    Button,
    Select,
    Flex,
    Box,
    Text,
    Stack,
    Loader,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { PriceSurvey } from '../../codegen/graphql';
import {
    useGetStoreManagers,
    useReassignSurveyChampion,
} from '../../services/survey';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../notifications';
import {
    formatManagerName,
    getSurveyAdvertisingZoneId,
    getSurveyScopeLabel,
    getSurveyStoreId,
    isStoreScopedSurvey,
} from '../../pages/price-surveys/helpers';

interface Props {
    survey: PriceSurvey;
    opened: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

interface FormValues {
    championUserId: string;
}

export function ReassignSurveyModal({
    survey,
    opened,
    onClose,
    onSuccess,
}: Props) {
    const form = useForm<FormValues>({
        initialValues: {
            championUserId: survey.championUserId || '',
        },
        validate: {
            championUserId: value =>
                !value ? 'A champion must be selected' : null,
        },
    });

    const isStoreScoped = isStoreScopedSurvey(survey);
    const surveyStoreId = getSurveyStoreId(survey);
    const surveyZoneId = getSurveyAdvertisingZoneId(survey);

    const { data: managersData, loading: loadingManagers } =
        useGetStoreManagers({
            skip: isStoreScoped ? !surveyStoreId : !surveyZoneId,
            advertisingZoneId: isStoreScoped ? undefined : surveyZoneId,
            storeId: isStoreScoped ? surveyStoreId : undefined,
        });

    const [reassignChampion, { loading: reassigning }] =
        useReassignSurveyChampion();

    const managers = useMemo(() => {
        return (
            managersData?.getUsers?.map(user => ({
                value: user.id,
                label: formatManagerName(user),
            })) || []
        );
    }, [managersData]);

    const currentChampionName = useMemo(() => {
        if (!survey.championUser) return 'Unassigned';
        return formatManagerName(survey.championUser);
    }, [survey.championUser]);

    const handleSubmit = async (values: FormValues) => {
        try {
            await reassignChampion({
                variables: {
                    input: {
                        surveyId: survey.id,
                        championUserId: values.championUserId,
                    },
                },
            });
            showSuccessNotification(
                'The survey champion has been successfully updated.',
            );
            onSuccess?.();
            onClose();
        } catch (error) {
            showErrorNotification(
                error instanceof Error ? error.message : 'Unknown error',
            );
        }
    };

    const scopeLabel = getSurveyScopeLabel(survey);
    const championScopeDescription = isStoreScoped
        ? `Only store managers at ${scopeLabel} are shown`
        : `Only store managers in zone ${scopeLabel} are shown`;

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            centered
            title="Reassign Survey Champion"
            size="md"
            radius="md"
            classNames={{
                title: 'text-2xl font-bold',
            }}
        >
            <Box bg="white" className="rounded-xl">
                <form onSubmit={form.onSubmit(handleSubmit)}>
                    <Stack gap="lg">
                        <Box>
                            <Text size="sm" c="dimmed">
                                Current Champion:
                            </Text>
                            <Text fw={600} size="md" c="primary">
                                {currentChampionName}
                            </Text>
                        </Box>

                        <Select
                            label="Select New Champion"
                            placeholder="Select manager"
                            description={championScopeDescription}
                            data={managers}
                            searchable
                            disabled={loadingManagers || reassigning}
                            rightSection={
                                loadingManagers && <Loader size="xs" />
                            }
                            nothingFoundMessage={
                                isStoreScoped
                                    ? 'No managers found for this store'
                                    : 'No managers found in this zone'
                            }
                            {...form.getInputProps('championUserId')}
                        />

                        <Flex justify="flex-end" gap="sm" mt="md">
                            <Button
                                variant="outline"
                                onClick={onClose}
                                disabled={reassigning}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={reassigning}
                                disabled={!form.isValid() || !form.isDirty()}
                            >
                                Submit
                            </Button>
                        </Flex>
                    </Stack>
                </form>
            </Box>
        </Modal>
    );
}
